import React, { useId, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { resolveNavQuery, SANS_RESULTAT_MESSAGE, searchCatalog, type NavSearchResult } from "../../nav/navSearch";
import { trackSearchUsed } from "../../helpers/analytics";

export const NavSearch = ({ onNavigate, autoFocus = false }: { onNavigate: (href: string) => void; autoFocus?: boolean }) => {
  const listId = useId();
  const items = useMemo(() => searchCatalog(), []);
  const [value, setValue] = useState("");
  const [selected, setSelected] = useState(-1);
  const [open, setOpen] = useState(false);
  const result: NavSearchResult | null = value.trim() === "" ? null : resolveNavQuery(value, new Date(), items);
  const suggestions = result?.suggestions ?? [];
  const visible = open && result !== null && (suggestions.length > 0 || result.type === "sans_resultat");

  const valider = (index: number) => {
    if (!result || suggestions.length === 0) return;
    trackSearchUsed(result.type);
    setOpen(false);
    onNavigate(suggestions[Math.max(0, index)].href);
  };

  return (
    <div className="km-search">
      <Search aria-hidden="true" />
      <input
        type="text" inputMode="search" autoComplete="off" spellCheck={false} autoFocus={autoFocus}
        role="combobox" aria-expanded={visible} aria-controls={listId} aria-autocomplete="list"
        aria-activedescendant={selected >= 0 ? `${listId}-${selected}` : undefined}
        aria-label="Chercher un numéro, une date ou une page" placeholder="Numéro ou date"
        value={value}
        onChange={(e) => { setValue(e.target.value); setSelected(-1); setOpen(true); }}
        onKeyDown={(e) => {
          const n = suggestions.length;
          if ((e.key === "ArrowDown" || e.key === "ArrowUp") && n > 0) {
            e.preventDefault();
            setSelected((s) => (s + (e.key === "ArrowDown" ? 1 : -1) + n) % n);
          } else if (e.key === "Enter") {
            e.preventDefault();
            valider(selected);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        onBlur={() => { if (result?.type === "sans_resultat") trackSearchUsed("sans_resultat"); setOpen(false); }}
      />
      <ul className="km-search__list" id={listId} role="listbox" aria-label="Suggestions" hidden={!visible}>
        {result?.type === "sans_resultat" && <li className="km-search__hint" role="presentation">{SANS_RESULTAT_MESSAGE}</li>}
        {suggestions.map((s, i) => (
          <li key={s.href} id={`${listId}-${i}`} className="km-search__opt" role="option" aria-selected={i === selected}
            onMouseDown={(e) => { e.preventDefault(); valider(i); }}>
            {s.label}{s.hint && <small>{s.hint}</small>}
          </li>
        ))}
      </ul>
    </div>
  );
};
